/** @jsx jsx */
import { Box, Container, Flex, Heading } from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import { FC, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { maxJobApplicationStepKey } from "@/data/job-applications/utils/maxJobApplicationStepKey";
import { JobRequest } from "@/data/job-requests/types/JobRequest";
import { useApplyWizard } from "@/views/job-applications/apply/_components/ApplyWizardContext";
import { AnswerFormValues } from "@/views/job-applications/apply/_questionnaire/ApplyQuestion";
import {
  ApplyQuestionnaireForm,
  questionnaireValidation,
  transformDraftWithQuestionnaire,
} from "@/views/job-applications/apply/_questionnaire/ApplyQuestionnaireForm";

import { ApplyWizardBackButton } from "../_components/ApplyWizardBackButton";

interface ApplyWizardQuestionnaireProps {
  jobRequest: JobRequest;
}

export const ApplyWizardQuestionnaire: FC<ApplyWizardQuestionnaireProps> = ({ jobRequest }) => {
  const { t } = useTranslation("session");
  const { createOrUpdateDraft, goToNextStep, draft, setDraft } = useApplyWizard();

  const isInitialRender = useRef(true);

  const FORM_EMPTY_INITIAL_VALUES = {
    answers: jobRequest.questions
      ? jobRequest.questions.map(question => ({
          question,
          answer: null,
          elaboration: "",
        }))
      : [],
  };

  const handleSubmit = useCallback(
    async (values: AnswerFormValues) => {
      try {
        await createOrUpdateDraft(draft => ({
          ...transformDraftWithQuestionnaire(values)(draft),
          step: maxJobApplicationStepKey(draft?.step, "QUESTIONNAIRE"),
        }));
      } catch (error) {
        // NOTE: errors are ignored on purpose
      } finally {
        goToNextStep();
      }
    },
    [createOrUpdateDraft, goToNextStep],
  );

  useEffect(() => {
    if (isInitialRender.current) {
      setDraft(d => ({ ...d!, step: maxJobApplicationStepKey(draft?.step, "QUESTIONNAIRE") }));
      isInitialRender.current = false;
    }
  });

  return (
    <Box py="md">
      <Container variant="narrow">
        <Flex px="sm" flexDirection="column" gap="sm">
          <ApplyWizardBackButton />

          <Heading size="h-md">{t("JOB_REQUESTS.APPLY.WIZARD.QUESTIONNAIRE")}</Heading>
          <Formik
            initialValues={{
              ...FORM_EMPTY_INITIAL_VALUES,
              answers: draft?.answers?.length ? draft.answers : FORM_EMPTY_INITIAL_VALUES.answers,
            }}
            validationSchema={questionnaireValidation}
            onSubmit={handleSubmit}>
            {(formikProps: FormikProps<AnswerFormValues>) => <ApplyQuestionnaireForm formik={formikProps} />}
          </Formik>
        </Flex>
      </Container>
    </Box>
  );
};
